import { clsx } from "@plans/appkit";
import * as RadixDialog from "@radix-ui/react-dialog";
import { RichText } from "~/components/atoms/RichText";
import { useDialog } from "~/lib/ui/DialogContext";
import { dialogActionSize } from "~/ui/constants";
import { Button } from "../atoms/Button";
import { Dialog, DialogClose, DialogContent } from "../atoms/Dialog";

/**
 * Dialog that is managed by a useDialog hook. This dialog will automatically open and close
 * based on the state of the useDialog hook.
 *
 * Useful for text dialogs that have simple UI.
 */
export function ManagedDialog() {
	const { currentDialog, ok, cancel } = useDialog();
	const onOpenChange = (open: boolean) => {
		if (!open) {
			cancel();
		}
	};

	const onOkButtonClick = () => {
		if (currentDialog?.type === "prompt") {
			const input = document.querySelector<
				HTMLInputElement | HTMLTextAreaElement
			>("#dialog-prompt-input");
			if (input) {
				ok(input.value);
			}
		} else {
			ok();
		}
	};

	// One class list for both, so a multiline prompt is the same control with
	// room to write in rather than a second field styled by hand.
	const promptFieldClassName =
		"global-focus border-background-medium w-full rounded-sm border p-2";

	return (
		<Dialog open={Boolean(currentDialog)} onOpenChange={onOpenChange}>
			<DialogContent
				testId="managed-dialog"
				title={<div className="text-center">{currentDialog?.heading}</div>}
				className="w-[510px]"
				isCloseOnInteractOutsideDisabled={true}
				// Clicking the backdrop is already inert for every dialog, so a
				// caller asking for a deliberate answer only has the X and Escape
				// left to take away.
				isCloseButtonHidden={currentDialog?.isDismissDisabled}
				isCloseOnEscapeDisabled={currentDialog?.isDismissDisabled}
				footerContent={
					<>
						{(currentDialog?.type === "confirm" ||
							currentDialog?.type === "prompt") && (
							<DialogClose asChild>
								<Button
									variant="secondary"
									size={dialogActionSize}
									testId="managed-dialog-cancel-button"
									className="flex-1"
								>
									{currentDialog.cancelLabel}
								</Button>
							</DialogClose>
						)}
						<Button
							variant="primary"
							size={dialogActionSize}
							testId="managed-dialog-ok-button"
							className="flex-1"
							onClick={onOkButtonClick}
						>
							{currentDialog?.okLabel}
						</Button>
					</>
				}
			>
				{currentDialog?.body && (
					<RadixDialog.Description asChild>
						<div className="-mt-1 pb-1.5 text-center">
							<RichText className="text-text-secondary text-base">
								{currentDialog.body}
							</RichText>
						</div>
					</RadixDialog.Description>
				)}
				{currentDialog?.type === "prompt" && (
					<div className="mt-5">
						{currentDialog.isMultiline ? (
							// Prose, not a short answer: a person writing a paragraph has to
							// be able to press return in it without submitting the dialog.
							<textarea
								id="dialog-prompt-input"
								rows={5}
								className={clsx(promptFieldClassName, "resize-y")}
								placeholder={currentDialog.placeholder}
								defaultValue={currentDialog.defaultValue}
							/>
						) : (
							<input
								id="dialog-prompt-input"
								type="text"
								className={promptFieldClassName}
								placeholder={currentDialog.placeholder}
								defaultValue={currentDialog.defaultValue}
							/>
						)}
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
